import {
  Box,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  GetResponse,
  TRANSACTION_PAYMENT,
  TRANSACTION_STATUS,
  TransactionProvider,
  WebpayMetadata,
} from 'lib/api/express/transaction/types';
import CustomButton from 'lib/designSystem/Button';
import React from 'react';

const statusTranslation = {
  [TRANSACTION_STATUS.AUTHORIZED]: 'Autorizado',
  [TRANSACTION_STATUS.REVERSED]: 'Reversado',
  [TRANSACTION_STATUS.FAILED]: 'Fallido',
  [TRANSACTION_STATUS.NULLIFIED]: 'Anulado',
  [TRANSACTION_STATUS.PARTIALLY_NULLIFIED]: 'Anulado parcialmente',
  [TRANSACTION_STATUS.CAPTURED]: 'Capturado',
  [TRANSACTION_STATUS.INITIALIZED]: 'Rechazado',
};

type Props = {
  data: GetResponse;
  handleReturn: () => void;
};

const Webpay: React.FC<Props> = ({ data, handleReturn }) => {
  if (!data) return null;

  if (data.provider === TransactionProvider.WEBPAY) {
    return (
      <WebpayMetadataView
        metadata={data.metadata}
        handleReturn={handleReturn}
      />
    );
  }

  return null;
};

type WebpayMetadataViewProps = {
  metadata: WebpayMetadata;
  handleReturn: () => void;
};

const WebpayMetadataView: React.FC<WebpayMetadataViewProps> = ({
  metadata,
  handleReturn,
}) => {
  const paymentTypeLabel = TRANSACTION_PAYMENT[metadata.payment_type_code];
  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'grey.200',
        bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>
          Detalle de la transacción Webpay
        </Typography>
        <Chip
          label={statusTranslation[metadata.status] ?? 'Rechazado'}
          color={metadata.status === 'AUTHORIZED' ? 'success' : 'default'}
          size="small"
          sx={{ textTransform: 'uppercase', fontWeight: 500 }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Información devuelta por Webpay Plus para esta transacción.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <MetadataItem
            label="Monto"
            value={new Intl.NumberFormat('es-CL', {
              style: 'currency',
              currency: 'CLP',
              maximumFractionDigits: 0,
            }).format(metadata.amount)}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <MetadataItem
            label="Tipo de pago"
            value={paymentTypeLabel}
            helperText={metadata.payment_type_code}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MetadataItem
            label="Fecha de transacción"
            value={new Date(metadata.transaction_date).toLocaleString('es-CL')}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MetadataItem label="Orden de compra" value={metadata.buy_order} />
        </Grid>

        <Grid item xs={12} sm={6}>
          <MetadataItem
            label="Número de tarjeta"
            value={
              metadata.card_detail?.card_number
                ? `**** **** **** ${metadata.card_detail.card_number}`
                : 'Sin información'
            }
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={handleReturn}
        >
          Volver
        </CustomButton>
      </Box>
    </Paper>
  );
};

type MetadataItemProps = {
  label: string;
  value: string;
  helperText?: string;
};

const MetadataItem: React.FC<MetadataItemProps> = ({
  label,
  value,
  helperText,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" spacing={0.5}>
      <Typography variant="body2" fontWeight={500}>
        {value}
      </Typography>
      {helperText && (
        <Typography variant="caption" color="text.disabled">
          ({helperText})
        </Typography>
      )}
    </Stack>
  </Box>
);

export default Webpay;
